/**
 * Bundle processor contracts
 * Defines interfaces for processing .md.zip bundles
 */

import type { Asset, AssetMap, BundleStructure } from './book.ts';

/**
 * Bundle processor interface
 */
export interface IBundleProcessor {
  /**
   * Extract a .md.zip bundle
   */
  extract(file: File | ArrayBuffer): Promise<BundleContent>;

  /**
   * Validate bundle structure
   */
  validate(file: File | ArrayBuffer): Promise<BundleValidationResult>;

  /**
   * Resolve asset path from markdown
   */
  resolveAssetPath(markdownPath: string, assetPath: string): string;
}

export interface BundleContent {
  /**
   * Content of README.md (required file in root)
   */
  readme: string;

  /**
   * Map of all assets in the bundle
   */
  assets: AssetMap;

  /**
   * Bundle structure information
   */
  structure: BundleStructure;

  /**
   * Validation result
   */
  validation: BundleValidationResult;
}

export interface BundleValidationResult {
  /**
   * Is the bundle valid?
   */
  valid: boolean;

  /**
   * Validation errors (if any)
   */
  errors: ValidationError[];

  /**
   * Validation warnings (non-critical issues)
   */
  warnings: ValidationWarning[];

  /**
   * Bundle metadata
   */
  metadata: BundleMetadata;
}

export interface ValidationError {
  /**
   * Error code
   */
  code: string;

  /**
   * Error message
   */
  message: string;

  /**
   * File path (if applicable)
   */
  file?: string;

  /**
   * Severity level
   */
  severity: 'error';
}

export interface ValidationWarning {
  /**
   * Warning code
   */
  code: string;

  /**
   * Warning message
   */
  message: string;

  /**
   * File path (if applicable)
   */
  file?: string;

  /**
   * Severity level
   */
  severity: 'warning';
}

export interface BundleMetadata {
  /**
   * Total number of files
   */
  fileCount: number;

  /**
   * Total number of directories
   */
  directoryCount: number;

  /**
   * Total size in bytes
   */
  totalSize: number;

  /**
   * Has README.md in root?
   */
  hasReadme: boolean;

  /**
   * Asset types found
   */
  assetTypes: {
    images: number;
    audio: number;
    video: number;
    documents: number;
    other: number;
  };
}

/**
 * Asset type detection
 */
export function getAssetType(mimeType: string): Asset['type'] {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('video/')) return 'video';
  if (
    mimeType.startsWith('text/') ||
    mimeType.includes('pdf') ||
    mimeType.includes('document')
  )
    return 'document';
  return 'other';
}

/**
 * MIME type detection from file extension
 */
export function getMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();

  const mimeTypes: Record<string, string> = {
    // Images
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',

    // Audio
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    m4a: 'audio/mp4',

    // Video
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogv: 'video/ogg',

    // Documents
    pdf: 'application/pdf',
    txt: 'text/plain',
    md: 'text/markdown',
    html: 'text/html',
    json: 'application/json',
  };

  return mimeTypes[ext ?? ''] ?? 'application/octet-stream';
}
